import { useMemo, useState, type FormEvent } from "react";
import { comments } from "@/mocks/comments";
import type { CommentItem } from "@/types";

interface DiscussionSectionProps {
  topicId: string;
}

const currentUser = "我";

export default function DiscussionSection({ topicId }: DiscussionSectionProps) {
  const initial = useMemo(
    () => comments.filter((item) => item.topicId === topicId),
    [topicId],
  );
  const [list, setList] = useState<CommentItem[]>(initial);
  const [draft, setDraft] = useState("");
  const [notice, setNotice] = useState("");
  const [liked, setLiked] = useState<Record<string, boolean>>({});

  const toggleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
    setList((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, likes: item.likes + (liked[id] ? -1 : 1) } : item,
      ),
    );
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content) {
      setNotice("先写点什么再发布吧。");
      return;
    }
    const newComment: CommentItem = {
      id: `local-${Date.now()}`,
      topicId,
      user: currentUser,
      time: "刚刚",
      content,
      likes: 0,
    };
    setList((prev) => [newComment, ...prev]);
    setDraft("");
    setNotice("观点已发布，现在可以在下方列表中看到。");
  };

  return (
    <section className="mt-10">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          观点碰撞
          <span className="ml-2 text-sm font-normal text-foreground-500">
            共 {list.length} 条讨论
          </span>
        </h2>
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
      >
        <label htmlFor="comment-draft" className="text-sm font-medium text-foreground-800">
          说说你的看法
        </label>
        <textarea
          id="comment-draft"
          name="comment"
          value={draft}
          maxLength={500}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          placeholder="围绕话题本身聊聊，对事不对人，理性表达更有说服力。"
          className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-6 text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
        />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-foreground-500">{draft.length}/500</span>
          <button
            type="submit"
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-send-plane-line"></i>
            发布观点
          </button>
        </div>
        {notice && <p className="mt-3 text-xs text-primary-700">{notice}</p>}
      </form>

      <ul className="mt-6 flex flex-col gap-4">
        {list.map((item) => (
          <li
            key={item.id}
            className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-700">
                {item.user.slice(0, 1)}
              </span>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-foreground-950">{item.user}</span>
                <span className="text-xs text-foreground-500">{item.time}</span>
              </div>
              <button
                type="button"
                aria-label={`为 ${item.user} 的评论点赞`}
                onClick={() => toggleLike(item.id)}
                className={`ml-auto flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  liked[item.id]
                    ? "border-accent-300 bg-accent-100 text-accent-700"
                    : "border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <i className={liked[item.id] ? "ri-heart-fill" : "ri-heart-line"}></i>
                {item.likes}
              </button>
            </div>
            <p className="mt-3 text-sm leading-7 text-foreground-700">{item.content}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}